/**
 * Boot timing marks. A mark records when a named boot step started or ended, so a
 * browser trace can show call order and repeats per navigation.
 *
 * Everything here is a no-op unless enabled, so production boot pays one flag read per call.
 * Enable in the browser with `localStorage.setItem('pufferfish.bootTiming', '1')` or by
 * setting `window.__PUFFERFISH_BOOT_TIMING__ = true` before the app loads.
 * Read the result with `window.__pufferfishBootMarks()` or `getBootMarks()`.
 */

export const BOOT_TIMING_STORAGE_KEY = 'pufferfish.bootTiming'

export interface BootMark {
    /** Step name, for example `platform_settings` or `access:abilities`. */
    name: string
    /** `start` and `end` bracket a step; `mark` is a point in time. */
    phase: 'start' | 'end' | 'mark'
    /** Milliseconds from the first mark of this page load. */
    at: number
    /** Set on `end` marks: milliseconds since the matching `start`. */
    duration?: number
    /** Set on `end` marks when the step failed. */
    failed?: boolean
    detail?: string
}

declare global {
    interface Window {
        __PUFFERFISH_BOOT_TIMING__?: boolean
        __pufferfishBootMarks?: () => BootMark[]
    }
}

let marks: BootMark[] = []
let origin: number | undefined
let userTimingAvailable = true
const openSpans = new Map<string, number[]>()

const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now())

export function isBootTimingEnabled(): boolean {
    try {
        if (typeof window === 'undefined') {
            return false
        }
        if (window.__PUFFERFISH_BOOT_TIMING__ === true) {
            return true
        }
        return window.localStorage?.getItem(BOOT_TIMING_STORAGE_KEY) === '1'
    } catch {
        return false
    }
}

function record(mark: Omit<BootMark, 'at'>, at: number): void {
    origin = origin ?? at
    marks.push({ ...mark, at: Math.round((at - origin) * 100) / 100 })

    // User Timing is optional: if it throws once, stop calling it and keep the in-memory marks.
    if (userTimingAvailable && typeof performance !== 'undefined' && typeof performance.mark === 'function') {
        try {
            performance.mark(`pufferfish:${mark.name}:${mark.phase}`)
        } catch {
            userTimingAvailable = false
        }
    }

    if (typeof window !== 'undefined') {
        window.__pufferfishBootMarks = getBootMarks
    }
}

/** Records a point in time. */
export function bootMark(name: string, detail?: string): void {
    if (!isBootTimingEnabled()) {
        return
    }
    record({ name, phase: 'mark', detail }, now())
}

/** Records the start of a step. Pair with `bootEnd`. Overlapping spans of one name end in start order. */
export function bootStart(name: string, detail?: string): void {
    if (!isBootTimingEnabled()) {
        return
    }
    const at = now()
    openSpans.set(name, [...(openSpans.get(name) ?? []), at])
    record({ name, phase: 'start', detail }, at)
}

/** Records the end of a step started with `bootStart`. */
export function bootEnd(name: string, outcome?: { failed?: boolean; detail?: string }): void {
    if (!isBootTimingEnabled()) {
        return
    }
    const at = now()
    const [startedAt, ...rest] = openSpans.get(name) ?? []
    openSpans.set(name, rest)
    record(
        {
            name,
            phase: 'end',
            duration: startedAt === undefined ? undefined : Math.round((at - startedAt) * 100) / 100,
            failed: outcome?.failed,
            detail: outcome?.detail,
        },
        at,
    )
}

/** Brackets an async step and returns its result unchanged. A rejection is recorded and rethrown. */
export async function bootSpan<T>(name: string, step: () => Promise<T>): Promise<T> {
    if (!isBootTimingEnabled()) {
        return step()
    }
    bootStart(name)
    try {
        const result = await step()
        bootEnd(name)
        return result
    } catch (error) {
        bootEnd(name, { failed: true, detail: error instanceof Error ? error.message : String(error) })
        throw error
    }
}

export function getBootMarks(): BootMark[] {
    return marks.map((mark) => ({ ...mark }))
}

export function resetBootMarks(): void {
    marks = []
    origin = undefined
    openSpans.clear()
}

/**
 * Request interceptors for an axios instance: marks each request start and end as
 * `request:<METHOD> <path>` with the query string removed. Returns a function that
 * removes them again.
 */
export function installBootRequestTiming(client: {
    interceptors: {
        request: { use: (fn: (c: any) => any) => number; eject: (id: number) => void }
        response: { use: (ok: (r: any) => any, err: (e: any) => any) => number; eject: (id: number) => void }
    }
}): () => void {
    const label = (config: any): string => {
        const path = String(config?.url ?? '')
            .replace(/^https?:\/\/[^/]+/i, '')
            .replace(/\?.*$/, '')
        return `request:${String(config?.method ?? 'get').toUpperCase()} ${path}`
    }

    const requestId = client.interceptors.request.use((config) => {
        bootStart(label(config))
        return config
    })
    const responseId = client.interceptors.response.use(
        (response) => {
            bootEnd(label(response?.config))
            return response
        },
        (error) => {
            bootEnd(label(error?.config), { failed: true, detail: error?.message })
            return Promise.reject(error)
        },
    )

    return () => {
        client.interceptors.request.eject(requestId)
        client.interceptors.response.eject(responseId)
    }
}
